/* eslint-disable react-hooks/exhaustive-deps */

import { Box, Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import { memo, useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';

import { fetchAPI } from '@/api/fetch';
import { apiUpdateJobCandidateNotes } from '@/api/services';
import { CandidateNote } from '@/types/Candidate';
import { CandidateNoteSection, getCandidateNoteSectionLabel } from '@/types/enums';
import RichEditor from '@/components/forms/RichEditor';
import { toaster } from '@/components/ui/toaster';

interface CandidateNotesProps {
  jobId: string;
  candidateId: string;
  notes: CandidateNote[];
}

const SAVE_DELAY = 1000;
const SAVED_MESSAGE_DURATION = 2000;

const CandidateNotes = memo(({ jobId, candidateId, notes }: CandidateNotesProps) => {
  const [section, setSection] = useState<CandidateNoteSection>(CandidateNoteSection.ResumeReview);
  const [noteList, setNoteList] = useState<CandidateNote[]>(notes);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    setNoteList(notes);
  }, [notes]);

  const handleTabChange = useCallback((tab: CandidateNoteSection) => {
    setSection(tab);
  }, []);

  const getNote = useCallback(() => {
    return noteList.find((note) => note.section === section)?.note || '';
  }, [noteList, section]);

  const showErrorToast = useCallback((message: string) => {
    toaster.error({
      title: 'Error',
      description: message,
      duration: 2000
    });
  }, []);

  const debouncedUpdateNotes = useCallback(
    debounce(async (noteList: CandidateNote[]) => {
      setSaveStatus('saving');

      try {
        const response = await fetchAPI<void>({
          request: () =>
            apiUpdateJobCandidateNotes({
              jobId,
              candidateId,
              payload: {
                notes: noteList
              }
            })
        });

        if (response.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), SAVED_MESSAGE_DURATION);
        } else {
          console.error('Failed to update notes', response.message);
          setSaveStatus('error');
          showErrorToast('Failed to update notes. Please try again.');
        }
      } catch (error) {
        console.error('Error updating meeting notes:', error);
        setSaveStatus('error');
        showErrorToast('An unexpected error occurred. Please try again.');
      }
    }, SAVE_DELAY),
    [jobId, candidateId, showErrorToast]
  );

  useEffect(() => {
    debouncedUpdateNotes(noteList);

    return () => {
      debouncedUpdateNotes.cancel();
    };
  }, [noteList, debouncedUpdateNotes]);

  const handleNoteChange = useCallback(
    (content: string) => {
      const updatedNote = noteList.map((note) => {
        if (note.section === section) {
          return { ...note, note: content };
        }
        return note;
      });
      setNoteList(updatedNote);
    },
    [noteList, section]
  );

  return (
    <Flex w="100%" mt="32px" gap="24px">
      <Box
        w="240px"
        minW="240px"
        h="100%"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
        p="16px 12px"
        role="navigation"
        aria-label="Note sections"
      >
        <Stack>
          {noteList.map((note, index) => (
            <Box
              key={index}
              p="16px 24px"
              borderRadius="12px"
              bg={section === note.section ? 'neutrals.100' : 'transparent'}
              cursor="pointer"
              onClick={() => handleTabChange(note.section)}
              role="button"
              tabIndex={0}
              aria-selected={section === note.section}
            >
              <Text
                color="neutrals.700"
                fontSize="14px"
                fontWeight={section === note.section ? 'semibold' : 'normal'}
              >
                {getCandidateNoteSectionLabel(note.section)}
              </Text>
            </Box>
          ))}
        </Stack>
      </Box>
      <Box
        w="100%"
        h="calc(100vh - 500px)"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
        role="region"
        aria-label={`${getCandidateNoteSectionLabel(section)} notes`}
      >
        <Stack gap="0">
          <Flex alignItems="center" h="72px" p="16px 24px" justifyContent="space-between">
            <Text fontSize="18px" fontWeight="semibold" color="neutrals.700">
              {getCandidateNoteSectionLabel(section)}
            </Text>
            {saveStatus === 'saving' && (
              <Flex alignItems="center" gap={2}>
                <Spinner size="sm" />
                <Text fontSize="14px" color="neutrals.500">
                  Saving...
                </Text>
              </Flex>
            )}
            {saveStatus === 'saved' && (
              <Text fontSize="14px" color="success.700">
                Saved
              </Text>
            )}
          </Flex>
          <Box w="100%" borderBottom="1px solid" borderColor="neutrals.200" />
          <Box p="16px 24px" w="100%">
            <RichEditor
              key={section}
              content={getNote()}
              type="candidate-details-note"
              placeholder="Write your notes about this candidate here..."
              onChange={handleNoteChange}
            />
          </Box>
          {saveStatus === 'error' && (
            <Text fontSize="16px" color="error.700" ml="24px" mt="2px" role="alert">
              Failed to update notes. Please try again.
            </Text>
          )}
        </Stack>
      </Box>
    </Flex>
  );
});

CandidateNotes.displayName = 'CandidateNotes';

export default CandidateNotes;
